import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Boxes, Plus, Edit, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { StudentPicker } from "@/pages/erp/Library";

const CATEGORIES = ["general", "it", "furniture", "lab", "sports", "vehicle"];
const CONDITIONS = ["good", "fair", "needs_repair", "damaged"];
const STATUSES = ["in_use", "in_storage", "retired", "lost"];

const statusColor = (s) => ({
  in_use: "border-emerald-300 text-emerald-700",
  in_storage: "border-blue-300 text-blue-700",
  retired: "border-slate-400 text-slate-600",
  lost: "border-red-300 text-red-700",
}[s] || "border-slate-300 text-slate-600");

const conditionColor = (c) => ({
  good: "border-emerald-300 text-emerald-700",
  fair: "border-amber-300 text-amber-700",
  needs_repair: "border-orange-300 text-orange-700",
  damaged: "border-red-300 text-red-700",
}[c] || "border-slate-300 text-slate-600");

const inr = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN")}`;

const EMPTY = { name: "", category: "general", code: "", purchase_date: "", cost: 0, location: "", assigned_to: "", assigned_student_id: "", assigned_class: "", assigned_section: "", condition: "good", status: "in_use", remarks: "" };

export default function Assets() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [st, setSt] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [assignStudent, setAssignStudent] = useState(false);
  const [studentsCache, setStudentsCache] = useState([]);

  // Resolve a student id → display name + class/section (for assignment)
  const resolveStudent = async (studentId) => {
    if (!studentId) return { assigned_to: "", assigned_student_id: "", assigned_class: "", assigned_section: "" };
    let s = studentsCache.find((x) => x.id === studentId);
    if (!s) {
      try {
        const { data } = await api.get(`/students/${studentId}`);
        s = data?.student || data;
      } catch { return { assigned_student_id: studentId }; }
    }
    setStudentsCache((c) => (s && !c.find((x) => x.id === s.id) ? [...c, s] : c));
    return {
      assigned_to: s ? `${s.first_name} ${s.last_name}` : "",
      assigned_student_id: studentId,
      assigned_class: s?.class_name || "",
      assigned_section: s?.section || "",
    };
  };

  const load = async () => {
    try {
      const params = {};
      if (cat !== "all") params.category = cat;
      if (st !== "all") params.status = st;
      const { data } = await api.get("/erp/assets", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, st]);

  const openNew = () => { setEditing(null); setF(EMPTY); setAssignStudent(false); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setF({ ...EMPTY, ...a, cost: a.cost || 0 });
    setAssignStudent(Boolean(a.assigned_student_id));
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, cost: Number(f.cost) || 0 };
      if (editing) await api.put(`/erp/assets/${editing.id}`, payload);
      else await api.post("/erp/assets", payload);
      toast.success(editing ? "Asset updated" : "Asset added");
      setOpen(false); setEditing(null); setF(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try { await api.delete(`/erp/assets/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = useMemo(() => items.filter((a) => {
    if (!q) return true;
    const hay = `${a.name} ${a.code} ${a.category} ${a.location} ${a.assigned_to}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [items, q]);

  const stats = useMemo(() => ({
    total: items.length,
    in_use: items.filter((a) => a.status === "in_use").length,
    needs_repair: items.filter((a) => a.condition === "needs_repair" || a.condition === "damaged").length,
    total_value: items.reduce((s, a) => s + (Number(a.cost) || 0), 0),
  }), [items]);

  return (
    <DashLayout variant="school" title="Asset Management">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Total Assets" value={stats.total} color="bg-blue-50 text-blue-700" testId="as-kpi-total" />
        <Kpi label="In Use" value={stats.in_use} color="bg-emerald-50 text-emerald-700" testId="as-kpi-in-use" />
        <Kpi label="Needs Repair" value={stats.needs_repair} color="bg-amber-50 text-amber-700" testId="as-kpi-repair" />
        <Kpi label="Total Value" value={inr(stats.total_value)} color="bg-purple-50 text-purple-700" testId="as-kpi-value" />
      </div>

      <Card className="p-3 flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input placeholder="Search name, code, location…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="as-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-40" data-testid="as-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={st} onValueChange={setSt}>
          <SelectTrigger className="w-36" data-testid="as-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="as-new"><Plus className="w-4 h-4 mr-1" /> New Asset</Button>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="hidden md:block">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Assigned To</TableHead>
              <TableHead>Condition</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`as-row-${a.id}`}>
                  <TableCell className="font-mono text-xs">{a.code || "—"}</TableCell>
                  <TableCell className="font-semibold">{a.name}</TableCell>
                  <TableCell><Badge variant="outline">{a.category}</Badge></TableCell>
                  <TableCell>{a.location || "—"}</TableCell>
                  <TableCell>{a.assigned_to || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={conditionColor(a.condition)}>{a.condition}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={statusColor(a.status)}>{a.status.replace("_", " ")}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{inr(a.cost)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`as-edit-${a.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(a.id)} data-testid={`as-del-${a.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500">No assets yet — click New Asset above</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="md:hidden divide-y divide-slate-100">
          {filtered.map((a) => (
            <div key={a.id} className="p-4" data-testid={`as-card-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{a.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.code || "—"} · {a.category}</div>
                </div>
                <Badge variant="outline" className={statusColor(a.status)}>{a.status.replace("_", " ")}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-600">
                <div><span className="text-slate-400">Location:</span> {a.location || "—"}</div>
                <div><span className="text-slate-400">Assigned:</span> {a.assigned_to || "—"}</div>
                <div><Badge variant="outline" className={conditionColor(a.condition)}>{a.condition}</Badge></div>
                <div className="text-right font-semibold tabular-nums">{inr(a.cost)}</div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(a)} data-testid={`as-card-edit-${a.id}`}><Edit className="w-4 h-4 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" className="flex-1 border-red-300 text-red-700" onClick={() => del(a.id)} data-testid={`as-card-del-${a.id}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">No assets yet</div>}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Asset" : "New Asset"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="as-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="mb-1 block text-xs">Name *</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="as-form-name" /></div>
              <div><Label className="mb-1 block text-xs">Code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="Auto if empty" data-testid="as-form-code" /></div>
              <div><Label className="mb-1 block text-xs">Category</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="as-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1 block text-xs">Purchase Date</Label><Input type="date" value={f.purchase_date} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} data-testid="as-form-date" /></div>
              <div><Label className="mb-1 block text-xs">Cost (₹)</Label><Input type="number" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} data-testid="as-form-cost" /></div>
              <div><Label className="mb-1 block text-xs">Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="as-form-location" /></div>
              <div className="col-span-2 border rounded-md p-3 bg-slate-50">
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer mb-2" data-testid="as-assign-toggle-wrap">
                  <input type="checkbox" checked={assignStudent} onChange={(e) => setAssignStudent(e.target.checked)} data-testid="as-assign-toggle" />
                  Assign to a student <span className="text-slate-400">(Class → Section → Student picker)</span>
                </label>
                {assignStudent ? (
                  <>
                    <StudentPicker
                      value={f.assigned_student_id || ""}
                      onChange={async (v) => setF({ ...f, ...(await resolveStudent(v)) })}
                      testPrefix="as-assign"
                    />
                    {f.assigned_student_id && (
                      <div className="text-xs text-emerald-700 mt-2" data-testid="as-assign-selected">
                        ✓ Assigned to <b>{f.assigned_to}</b> · Class {f.assigned_class} {f.assigned_section}
                      </div>
                    )}
                  </>
                ) : (
                  <div><Label className="mb-1 block text-xs">Assigned To (free text)</Label><Input value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value, assigned_student_id: "", assigned_class: "", assigned_section: "" })} placeholder="e.g. Rakesh Sharma / Sports Room" data-testid="as-form-assigned" /></div>
                )}
              </div>
              <div><Label className="mb-1 block text-xs">Condition</Label>
                <Select value={f.condition} onValueChange={(v) => setF({ ...f, condition: v })}>
                  <SelectTrigger data-testid="as-form-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1 block text-xs">Status</Label>
                <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                  <SelectTrigger data-testid="as-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="mb-1 block text-xs">Remarks</Label><Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="as-form-remarks" /></div>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="as-form-submit"><Boxes className="w-4 h-4 mr-1" /> {editing ? "Update" : "Add Asset"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}

const Kpi = ({ label, value, color, testId }) => (
  <Card className="p-3" data-testid={testId}>
    <div className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${color}`}>{label}</div>
    <div className="text-2xl font-black mt-1 tabular-nums">{value}</div>
  </Card>
);
